import api, { ResponsePayloadWithData } from '.';

interface SchoolInGroup {
  id: string;
  name: string;
  cities?: string;
  isSmartschool: boolean;
}

export type GroupStatus = 'unpublished' | 'published' | 'closed';

export interface Group {
  id: string;
  name: string;
  school: SchoolInGroup;
  subjects: string[];
  schoolYear: string;
  totalAccepted: number;
  totalPending: number;
  totalDeclined: number;
  invitationId: string;
  smartschoolGroupId?: string;
  status?: GroupStatus;
  code?: string;
  startDate?: string;
  endDate?: string;
}

export interface School {
  id: string;
  name: string;
  cities?: string;
  isSmartschool: boolean;
}

export type CandidateGroup = {
  id: string;
  smartschoolGroupId: string;
  smartschoolSchoolId: string;
  name: string;
  schoolYear: string;
};

// Simplified version of Group for /current/groups
export interface CurrentGroup {
  id: string;
  name: string;
  school: School;
  schoolYear: string;
  isSmartschoolSynced: boolean;
  acceptedMemberCount: number;
}

export interface CurrentOrPreviousGroup extends CurrentGroup {}

/**
 * @returns An alphabetically (by name) sorted array of groups
 */
export async function fetchCurrentAndPreviousGroups(): Promise<CurrentOrPreviousGroup[]> {
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<CurrentOrPreviousGroup[]>>(
    '/studio/user/school-years/current/previous/groups',
    { params: { studioGroup: process.env.REACT_APP_P_STUDIO_GROUP } },
  );

  return data;
}

export async function fetchCurrentGroups(): Promise<CurrentGroup[]> {
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<CurrentGroup[]>>('/studio/user/school-years/current/groups', {
    params: { studioGroup: process.env.REACT_APP_P_STUDIO_GROUP },
  });

  return data;
}
export interface Member {
  id: string;
  primaryEmail?: string;
  status: 'accepted' | 'pending' | 'declined';
  firstName?: string;
  lastName?: string;
  password?: string;
  username?: string;
}

interface SchoolYear {
  startYear: number;
  endYear: number;
}

export async function fetchMembers(schoolYear: SchoolYear | string, groupId: string): Promise<Member[]> {
  const schoolYearString =
    typeof schoolYear === 'string' ? schoolYear : `${schoolYear.startYear}-${schoolYear.endYear}`;
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<Member[]>>(
    `studio/user/school-years/${schoolYearString}/groups/${groupId}/members`,
  );

  return data;
}

export async function fetchGroups(schoolYear: SchoolYear): Promise<Group[]> {
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<Group[]>>(
    `studio/user/school-years/${schoolYear.startYear}-${schoolYear.endYear}/groups`,
    {
      params: { studioGroup: process.env.REACT_APP_P_STUDIO_GROUP },
    },
  );

  return data;
}

export async function fetchCandidateGroups(): Promise<CandidateGroup[]> {
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<CandidateGroup[]>>('studio/user/school-years/current/candidate-groups');

  return data;
}

export async function createGroupsFromCandidateGroups(
  schoolYear: SchoolYear,
  groupIds: string[],
  subjects: string[],
): Promise<void> {
  const { data } = await api.post(
    `/studio/user/school-years/${schoolYear.startYear}-${schoolYear.endYear}/batch-smartschool-groups`,
    {
      groupIds,
      subjects,
    },
  );
  return data;
}

export async function linkToGroup(schoolYear: string, groupId: string, email: string): Promise<Member> {
  const {
    data: { data },
  } = await api.post<ResponsePayloadWithData<Member>>(
    `studio/user/school-years/${schoolYear}/groups/${groupId}/members/link`,
    { email },
  );

  return data;
}

export async function removeMemberFromGroup(schoolYear: string, groupId: string, memberId: string): Promise<void> {
  await api.delete(`studio/user/school-years/${schoolYear}/groups/${groupId}/members/${memberId}`);
}

export async function getVOExportFile(schoolYear: string, groupId: string): Promise<Blob> {
  const { data } = await api.get(`studio/user/school-years/${schoolYear}/groups/${groupId}/members/export`, {
    responseType: 'arraybuffer',
  });

  return new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
